import { EntityDrawerProps, FCC, FormDrawer, FormDrawerContent, Icon, Id, Label, TextButton, Trigger } from "@dgs/core";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { TagMultiSelectField } from "../../../../shared/ui/TagSelectField";
import { TagIndexResource, TagOptionResource, TagsGroupedByCategory } from "../types";
import { ViewPageSection, ViewPageSectionBody, ViewPageSectionHeader, ViewPageSectionItem } from "./ViewComponents";

interface Props {
	tags: TagOptionResource[];
	onUpdateTags?: (tags: TagIndexResource[]) => Promise<boolean>;
}

interface FormValues {
	tags: TagIndexResource[];
}

const EditTagsDrawer: FCC<EntityDrawerProps<FormValues>> = (props) => {
	const { t } = useTranslation();

	return (
		<FormDrawer
			{...props}
			renderForm={(props) => (
				<FormDrawerContent {...props}>
					<TagMultiSelectField label={t("Tags")} name="tags" />
				</FormDrawerContent>
			)}
		/>
	);
};

const TagList = styled.ul`
	display: flex;
	list-style: none;
	margin: ${({ theme }) => theme.spacing(1)} 0;
	gap: ${({ theme }) => theme.spacing(2)};
`;

export const ViewTagsSection: FC<Props> = ({ tags, onUpdateTags }) => {
	const { t } = useTranslation();
	const tagsGroupedByCategory = useMemo(() => {
		return tags.reduce(
			(collected, tag) => {
				const categoryId = tag.category?.id || "uncategorized";
				const grouped = collected[categoryId] || { category: tag.category || "uncategorized", tags: [] };

				return { ...collected, [categoryId]: { ...grouped, tags: [...grouped.tags, tag] } };
			},
			{} as Record<Id | "uncategorized", TagsGroupedByCategory>,
		);
	}, [tags]);

	return (
		<ViewPageSection style={{ gridArea: "tags" }}>
			<ViewPageSectionHeader>{t("Tags")}</ViewPageSectionHeader>
			<ViewPageSectionBody>
				{Object.entries(tagsGroupedByCategory).map(([categoryId, { category, tags }]) => (
					<ViewPageSectionItem
						key={categoryId}
						aria-label={category === "uncategorized" ? t("uncategorized") : category.name}
					>
						{category !== "uncategorized" && category.name}
						<TagList>
							{tags.map((tag) => (
								<li key={tag.id}>
									<Label>{tag.name}</Label>
								</li>
							))}
						</TagList>
					</ViewPageSectionItem>
				))}
				{onUpdateTags && (
					<ViewPageSectionItem>
						<Trigger
							render={(props) => (
								<EditTagsDrawer
									open={props.open}
									onClose={props.handleClose}
									title={t("Tags")}
									initialValues={{ tags }}
									onSubmit={({ tags }) => onUpdateTags(tags)}
									onClosed={() => void 0}
								/>
							)}
						>
							{(props) => (
								<TextButton {...props} color="primary" prefix={<Icon $icon="edit" />}>
									{t("Edit tags")}
								</TextButton>
							)}
						</Trigger>
					</ViewPageSectionItem>
				)}
			</ViewPageSectionBody>
		</ViewPageSection>
	);
};
